import { computed, inject, Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { Dtos, GroupedSection, GroupedSubSection } from '#models';
import { Server } from '#enums';
import { StoreService } from './store';

@Injectable({
  providedIn: 'root',
})
export class ResponseValidator {
  static readonly ActionItemIsNeededError: ValidationErrors = {
    ResponseText: {
      invalid: true,
      message: 'pssrDetails.questions.validation.actionItemIsNeeded',
    },
  };

  static readonly CommentIsNeededError: ValidationErrors = {
    ResponseText: {
      invalid: true,
      message: 'pssrDetails.questions.validation.commentIsNeeded',
    },
  };

  static readonly SignatureIsNeededError: ValidationErrors = {
    ResponseText: {
      invalid: true,
      message: 'pssrDetails.questions.validation.signatureIsNeeded',
    },
  };

  static readonly EmptyQuestionError: ValidationErrors = {
    ResponseText: {
      invalid: true,
      message: 'pssrDetails.questions.validation.responseIsNeeded',
    },
  };

  private static readonly ActionItemsNotClosedOrCancelledErrorMessage =
    'pssrDetails.questions.validation.actionItemsClosedOrCancelledNeeded';

  validate(pssrResponse: Dtos.PSSRResponse, bu: Dtos.BusinessUnit): ValidationErrors | null {
    // Rule 1 (legacy): Yes answer on Default question is valid.
    if (this.hasResponseYes(pssrResponse)) {
      return null;
    }

    // Apply validation rules in sequence
    const validationRules = [
      // Rule 2: No answer requires action item
      {
        condition: () => this.hasResponseNoWithoutActionItems(pssrResponse),
        error: ResponseValidator.ActionItemIsNeededError,
      },
      // Rule 3: N/A requires action item or comment when BU doesn't allow N/A
      {
        condition: () => this.isActionItemNeededForNa(pssrResponse, bu),
        error: ResponseValidator.ActionItemIsNeededError,
      },
      // Rule 4: N/A with mandatory comment requires comment
      { condition: () => this.isCommentNeededForNa(pssrResponse), error: ResponseValidator.CommentIsNeededError },
      // Rule 5: Summary signature requires image
      {
        condition: () => this.isSignatureNeededForSummary(pssrResponse),
        error: ResponseValidator.SignatureIsNeededError,
      },
      // Rule 6: Action item container validation
      { condition: () => this.isActionItemsContainerWithOpenOrRejectedActionItems(pssrResponse, bu), error: null },
      // Rule 7: Default question requires response
      { condition: () => this.isEmptyQuestion(pssrResponse), error: ResponseValidator.EmptyQuestionError },
    ];

    for (const rule of validationRules) {
      if (rule.condition()) {
        // Special handling for Rule 6 which needs dynamic error
        if (rule.error === null && this.isActionItemsContainerWithOpenOrRejectedActionItems(pssrResponse, bu)) {
          return this.getActionItemsNotClosedOrCancelledError(bu);
        }
        return rule.error;
      }
    }

    return null;
  }

  private hasResponseYes(response: Dtos.PSSRResponse): boolean {
    return response.ResponseText === 'Yes' && response.ItemType === Server.ItemType.Default;
  }

  private hasResponseNoWithoutActionItems(response: Dtos.PSSRResponse): boolean {
    return (
      response.ResponseText === 'No' && response.ItemType === Server.ItemType.Default && !this.hasActionItems(response)
    );
  }

  private hasActionItems(response: Dtos.PSSRResponse): boolean {
    return !!response.ActionItems?.some(item => item.IsActionItem);
  }

  private hasComments(response: Dtos.PSSRResponse): boolean {
    return !!response.ActionItems?.some(item => !item.IsActionItem);
  }

  private isActionItemNeededForNa(response: Dtos.PSSRResponse, businessUnit: Dtos.BusinessUnit): boolean {
    return (
      response.ResponseText === 'N/A' &&
      response.ItemType === Server.ItemType.Default &&
      !response.ShouldAddCommentToQuestion &&
      !businessUnit.AllowNAResponses &&
      !this.hasActionItems(response) &&
      !this.hasComments(response)
    );
  }

  private isCommentNeededForNa(response: Dtos.PSSRResponse): boolean {
    return (
      response.ResponseText === 'N/A' &&
      response.ItemType === Server.ItemType.Default &&
      !!response.ShouldAddCommentToQuestion &&
      !this.hasComments(response)
    );
  }

  private isSignatureNeededForSummary(response: Dtos.PSSRResponse): boolean {
    return response.ItemType === Server.ItemType.Signature && this.isSummarySignatureNeeded(response);
  }

  private isActionItemsContainerWithOpenOrRejectedActionItems(
    response: Dtos.PSSRResponse,
    businessUnit: Dtos.BusinessUnit
  ): boolean {
    return (
      !response?.ItemDescription &&
      this.hasActionItems(response) &&
      this.hasOpenOrRejectedActionItems(response, businessUnit)
    );
  }

  private hasOpenOrRejectedActionItems(response: Dtos.PSSRResponse, businessUnit: Dtos.BusinessUnit): boolean {
    const prioritiesNeedingAttention =
      businessUnit?.Code === Server.BusinessUnitCode.TCO
        ? [Server.ResponseItemPriority.P3, Server.ResponseItemPriority.P4]
        : [Server.ResponseItemPriority.P3];

    return !!response.ActionItems?.some(
      item =>
        item.IsActionItem &&
        prioritiesNeedingAttention.includes(item.Priority) &&
        (item.Status === Server.ResponseItemStatus.Open || item.Status === Server.ResponseItemStatus.Rejected)
    );
  }

  private getActionItemsNotClosedOrCancelledError(businessUnit: Dtos.BusinessUnit): ValidationErrors {
    const p3Label = businessUnit?.P3Label || 'P3';
    return {
      ResponseText: {
        invalid: true,
        message: ResponseValidator.ActionItemsNotClosedOrCancelledErrorMessage,
        messageParams: {
          P3Label: p3Label,
        },
      },
    };
  }

  private isEmptyQuestion(response: Dtos.PSSRResponse): boolean {
    return response.ItemType === Server.ItemType.Default && !response.ResponseText;
  }

  hasActionItemsNeeded(responses: Dtos.PSSRResponse[]): boolean {
    return responses.some(response => {
      const hasNoAnswer = response.ResponseText?.toLowerCase() === 'no';
      const hasNoActionItems = !response.ActionItems || response.ActionItems.length === 0;
      return hasNoAnswer && hasNoActionItems;
    });
  }

  calculateActionItemCounts(responses: Dtos.PSSRResponse[]): {
    p3Closed: number;
    p3Total: number;
    p4Closed: number;
    p4Total: number;
    p5Closed: number;
    p5Total: number;
  } {
    let p3Total = 0;
    let p3Closed = 0;
    let p4Total = 0;
    let p4Closed = 0;
    let p5Total = 0;
    let p5Closed = 0;

    responses.forEach(response => {
      if (!response.ActionItems || response.ActionItems.length === 0) {
        return;
      }

      response.ActionItems.forEach(item => {
        if (!item.IsActionItem) {
          return;
        }

        if (item.Priority === Server.ResponseItemPriority.P3) {
          p3Total++;
          if (item.Status === Server.ResponseItemStatus.Closed) {
            p3Closed++;
          }
        } else if (item.Priority === Server.ResponseItemPriority.P4) {
          p4Total++;
          if (item.Status === Server.ResponseItemStatus.Closed) {
            p4Closed++;
          }
        } else if (item.Priority === Server.ResponseItemPriority.P5) {
          p5Total++;
          if (item.Status === Server.ResponseItemStatus.Closed) {
            p5Closed++;
          }
        }
      });
    });

    return { p3Total, p3Closed, p4Total, p4Closed, p5Total, p5Closed };
  }

  /**
   * Calculate the status of a single action item (ResponseItem)
   * @param item - The action item to evaluate
   * @returns ResponseStatus (Green, Orange, or Red)
   */
  getItemStatus(item: Dtos.ResponseItem): Server.ResponseStatus {
    // Comments: check if it has text
    if (!item.IsActionItem) {
      return this.getCommentStatus(item);
    }

    return this.getActionItemStatusByState(item);
  }

  /**
   * Get status for a comment item
   */
  private getCommentStatus(item: Dtos.ResponseItem): Server.ResponseStatus {
    // If comment has no text, needs attention (Red)
    if (!item.Comment || item.Comment.trim() === '') {
      return Server.ResponseStatus.NeedUserAttention; // Red
    }
    return Server.ResponseStatus.Answered; // Green
  }

  /**
   * Get status for an action item based on its state and priority
   */
  private getActionItemStatusByState(item: Dtos.ResponseItem): Server.ResponseStatus {
    // Closed or Cancelled items are complete (Green)
    if (this.isActionItemComplete(item)) {
      return Server.ResponseStatus.Answered; // Green
    }

    // P3 items (Open or Rejected) need user attention (Red)
    if (item.Priority === Server.ResponseItemPriority.P3) {
      return Server.ResponseStatus.NeedUserAttention; // Red
    }

    // P4 or P5 items (Open or Rejected) are optional (Orange)
    if (this.isOptionalPriority(item)) {
      return Server.ResponseStatus.Optional; // Orange
    }

    // Default to answered/complete (Green)
    return Server.ResponseStatus.Answered; // Green
  }

  /**
   * Check if action item is complete (Closed or Cancelled)
   */
  private isActionItemComplete(item: Dtos.ResponseItem): boolean {
    return item.Status === Server.ResponseItemStatus.Closed || item.Status === Server.ResponseItemStatus.Cancel;
  }

  /**
   * Check if action item has optional priority (P4 or P5)
   */
  private isOptionalPriority(item: Dtos.ResponseItem): boolean {
    return item.Priority === Server.ResponseItemPriority.P4 || item.Priority === Server.ResponseItemPriority.P5;
  }

  /**
   * Check if action items have at least one valid signature image
   * A valid signature image must have both Photo and Caption
   * @param actionItems - Array of action items to check
   * @returns true if at least one action item has a valid signature image
   */
  private hasValidSignatureImage(actionItems: Dtos.ResponseItem[]): boolean {
    if (!actionItems || actionItems.length === 0) {
      return false;
    }

    return actionItems.some((item: Dtos.ResponseItem) => {
      if (!item?.Images || item.Images.length === 0) {
        return false;
      }

      return (
        item.Images.length > 0 &&
        item.Images.some(
          (img: Dtos.ResponseItemImage) =>
            img?.Photo && img.Photo.trim() !== '' && img?.Caption && img.Caption.trim() !== ''
        )
      );
    });
  }

  /**
   * Check if Summary Signature needs user attention
   * Summary Signature is GREEN if at least one action item has a valid signature image
   * @param response - The PSSRResponse to check
   * @returns true if signature image is needed (RED), false if satisfied (GREEN)
   */
  isSummarySignatureNeeded(response: Dtos.PSSRResponse): boolean {
    // Summary signature needs user attention if no valid signature images exist
    return !this.hasValidSignatureImage(response.ActionItems);
  }

  /**
   * Validate Walkdown Signature
   * Walkdown is GREEN if: no action items exist OR at least one action item has valid signature image
   * Walkdown is RED if: action items exist but none have valid signature images
   * @param response - The PSSRResponse to validate
   * @returns true if validation fails (RED), false if validation passes (GREEN)
   */
  validateWalkdownSignature(response: Dtos.PSSRResponse): boolean {
    // If no action items, walkdown is valid (GREEN)
    if (!response.ActionItems || response.ActionItems.length === 0) {
      return false; // No validation failure
    }

    // If action items exist, check if at least one has a valid signature image
    const hasValidImage = this.hasValidSignatureImage(response.ActionItems);
    return !hasValidImage; // Return true (RED) if no valid image found
  }

  /**
   * Calculate the status of a response/question based on its answer and action items
   * @param response - The PSSRResponse to evaluate
   * @param _bu - Business unit for validation rules
   * @returns ResponseStatus (Green, Orange, or Red)
   */
  getResponseStatus(response: Dtos.PSSRResponse, _bu: Dtos.BusinessUnit): Server.ResponseStatus {
    // Handle Summary Signature (ItemType.Signature)

    if (response.ItemType === Server.ItemType.Signature) {
      return this.getSummarySignatureStatus(response);
    }

    // Handle Walkdown Signature (ItemType.WalkdownSignature)
    if (response.ItemType === Server.ItemType.WalkdownSignature) {
      return this.getWalkdownSignatureStatus(response);
    }

    // Regular response (non-signature) handling
    return this.getRegularResponseStatus(response);
  }

  /**
   * Get status for Summary Signature responses
   */
  private getSummarySignatureStatus(response: Dtos.PSSRResponse): Server.ResponseStatus {
    // Summary signature needs valid signature image to be GREEN
    if (this.isSummarySignatureNeeded(response)) {
      return Server.ResponseStatus.NeedUserAttention; // Red
    }
    // Action items under Summary Signatures don't affect status
    return Server.ResponseStatus.Answered; // Green
  }

  /**
   * Get status for Walkdown Signature responses
   */
  private getWalkdownSignatureStatus(response: Dtos.PSSRResponse): Server.ResponseStatus {
    // Check walkdown signature validation first
    if (this.validateWalkdownSignature(response)) {
      return Server.ResponseStatus.Optional; // Orange - optional when signature image is missing
    }

    return Server.ResponseStatus.Answered; // Green
  }

  /**
   * Check if response has P3 action items that are Open or Rejected
   */
  private hasP3ActionItemsNeedingAttention(response: Dtos.PSSRResponse): boolean {
    if (!response.ActionItems || response.ActionItems.length === 0) {
      return false;
    }

    return response.ActionItems.some(
      (ai: Dtos.ResponseItem) =>
        ai.IsActionItem &&
        ai.Priority === Server.ResponseItemPriority.P3 &&
        (ai.Status === Server.ResponseItemStatus.Open || ai.Status === Server.ResponseItemStatus.Rejected)
    );
  }

  /**
   * Get status for regular (non-signature) responses
   */
  private getRegularResponseStatus(response: Dtos.PSSRResponse): Server.ResponseStatus {
    // If response text is empty or null, needs user attention (Red)
    if (this.isResponseTextEmpty(response)) {
      return Server.ResponseStatus.NeedUserAttention; // Red
    }

    // If response is "No" but has no action items, needs user attention (Red)
    if (this.isNoResponseWithoutActionItems(response)) {
      return Server.ResponseStatus.NeedUserAttention; // Red
    }

    const bu = this._storeService.currentBu();
    if ((bu && this.isActionItemNeededForNa(response, bu)) || this.isCommentNeededForNa(response)) {
      return Server.ResponseStatus.NeedUserAttention; // Red
    }

    // If response has action items, get the worst status from them
    return this.getWorstActionItemStatus(response);
  }

  /**
   * Check if response text is empty
   */
  private isResponseTextEmpty(response: Dtos.PSSRResponse): boolean {
    return !response.ResponseText || response.ResponseText.trim() === '';
  }

  /**
   * Check if response is "No" without action items
   */
  private isNoResponseWithoutActionItems(response: Dtos.PSSRResponse): boolean {
    return (
      !!response.ResponseText &&
      response.ResponseText.toLowerCase() === 'no' &&
      (!response.ActionItems || response.ActionItems.length === 0)
    );
  }

  /**
   * Get the worst status from all action items in the response
   */
  private getWorstActionItemStatus(response: Dtos.PSSRResponse): Server.ResponseStatus {
    if (!response.ActionItems || response.ActionItems.length === 0) {
      return Server.ResponseStatus.Answered; // Green
    }

    const actionItemStatuses = response.ActionItems.filter((ai: Dtos.ResponseItem) => ai.IsActionItem).map(
      (ai: Dtos.ResponseItem) => this.getItemStatus(ai)
    );

    if (actionItemStatuses.length > 0) {
      // Return the maximum (worst) status
      return Math.max(...actionItemStatuses) as Server.ResponseStatus;
    }

    // Otherwise, response is answered (Green)
    return Server.ResponseStatus.Answered; // Green
  }

  /**
   * Convert ResponseStatus enum to CSS class name for colored borders
   * @param status - ResponseStatus enum value
   * @returns CSS class name
   */
  getResponseStatusCSSClass(status: Server.ResponseStatus): string {
    switch (status) {
    case Server.ResponseStatus.Answered:
      return 'greenResponse'; // Green border
    case Server.ResponseStatus.Optional:
      return 'orangeResponse'; // Orange border
    case Server.ResponseStatus.NeedUserAttention:
      return 'redResponse'; // Red border
    default:
      return ''; // No border
    }
  }
  /**
   * Aggregate action item statuses to determine header color class.
   * Priority: Red > Orange > Green
   * @param actionItems - Array of ResponseItem action items
   * @returns CSS class name for the most critical status found
   */
  aggregateActionItemStatusClass(actionItems: Dtos.ResponseItem[]): string {
    if (!actionItems || actionItems.length === 0) {
      return 'greenResponse';
    }

    let hasOrange = false;
    let hasGreen = false;

    for (const item of actionItems) {
      const status = item.ResponseStatus || this.getItemStatus(item);

      if (status === Server.ResponseStatus.NeedUserAttention) {
        return 'redResponse';
      }

      if (status === Server.ResponseStatus.Optional) {
        hasOrange = true;
      } else if (status === Server.ResponseStatus.Answered) {
        hasGreen = true;
      }
    }

    if (hasOrange) return 'orangeResponse';
    if (hasGreen) return 'greenResponse';

    return '';
  }

  private sortBySortId<T extends { SortID: number }>(a: T, b: T): number {
    return a.SortID - b.SortID;
  }
  /**
   * Group responses by section and calculate statuses
   * @param responses - Array of PSSR responses to group
   * @param allTaskPlanSections - Array of all task plan sections (including Signatures)
   * @param standAloneActionItems - Optional standalone action items
   * @returns Array of grouped sections sorted by SortID
   */
  groupResponsesBySection(
    responses: Dtos.PSSRResponse[],
    allTaskPlanSections: Dtos.TaskPlanSection[],
    standAloneActionItems: Dtos.ResponseItem[] = []
  ): GroupedSection[] {
    let sections = this.extractSectionsFromResponses(responses, allTaskPlanSections);
    sections = this.appendProjectSpecificSection(sections);
    sections = this.appendStandAloneActionItemsSection(sections, standAloneActionItems);
    sections = this.appendSignaturesSection(sections);

    // Sort sections by SortID
    return this.sort(sections, this.sortBySortId);
  }

  /**
   * Extract sections from responses with subsections and calculated status
   */
  private extractSectionsFromResponses(
    responses: Dtos.PSSRResponse[],
    allTaskPlanSections: Dtos.TaskPlanSection[]
  ): GroupedSection[] {
    const sectionIds = new Set(responses.map(r => `${r.SectionID}-${r.SectionDescription}`));
    const sections: GroupedSection[] = [];

    for (const sectionId of sectionIds) {
      const sectionResponses = responses.filter(r => `${r.SectionID}-${r.SectionDescription}` === sectionId);
      const section = this.extractSection(sectionId, sectionResponses, allTaskPlanSections);
      sections.push(section);
    }

    return sections;
  }
  appendStandAloneActionItemsSection(
    sections: GroupedSection[],
    standAloneActionItems: Dtos.ResponseItem[]
  ): GroupedSection[] {
    // Don't add if section already exists
    if (this.hasStandAloneSection(sections)) {
      return sections;
    }

    const pssr = this._storeService.currentPssr();
    if (!pssr) {
      return sections;
    }

    // Don't add if PSSR is approved with no action items
    if (this.shouldSkipStandAloneSection(pssr, standAloneActionItems)) {
      return sections;
    }

    // Only add for completed or later status
    if (pssr.Status < Server.PSSRStatus.Completed) {
      return sections;
    }

    const virtualResponse = this.createStandAloneVirtualResponse(pssr, sections, standAloneActionItems);
    const standAloneSection = this.createStandAloneSection(sections, virtualResponse);

    return [...sections, standAloneSection];
  }

  /**
   * Check if standalone section already exists
   */
  private hasStandAloneSection(sections: GroupedSection[]): boolean {
    return sections.some(s => s.Description === 'Post-Walkdown Action Items');
  }

  /**
   * Check if standalone section should be skipped
   */
  private shouldSkipStandAloneSection(pssr: Dtos.PSSR, standAloneActionItems: Dtos.ResponseItem[]): boolean {
    return pssr.Status >= Server.PSSRStatus.Approved && standAloneActionItems.length === 0;
  }

  /**
   * Create virtual response for standalone action items
   */
  private createStandAloneVirtualResponse(
    pssr: Dtos.PSSR,
    sections: GroupedSection[],
    standAloneActionItems: Dtos.ResponseItem[]
  ): Dtos.PSSRResponse {
    const virtualResponse = {
      Id: null,
      PSSRID: pssr.Id || 0,
      SectionID: 0,
      SectionDescription: 'Post-Walkdown Action Items',
      SectionSortID: sections.length > 0 ? Math.max(...sections.map(s => s.SortID)) + 1 : 1,
      SubSectionDescription: 'Post-Walkdown Action Items',
      SubSectionSortID: 0,
      ItemType: Server.ItemType.ActionItem,
      ResponseText: '',
      ActionItems: standAloneActionItems,
    } as Dtos.PSSRResponse;

    virtualResponse.ResponseStatus = this.getResponseStatus(virtualResponse, this._storeService.currentBu()!);
    return virtualResponse;
  }

  /**
   * Create standalone section for post-walkdown action items
   */
  private createStandAloneSection(sections: GroupedSection[], virtualResponse: Dtos.PSSRResponse): GroupedSection {
    return {
      ID: 0,
      Description: 'Post-Walkdown Action Items',
      SortID: sections.length > 0 ? Math.max(...sections.map(s => s.SortID)) + 1 : 1,
      Responses: [virtualResponse],
      SubSections: [
        {
          Key: 'Post-Walkdown Action Items',
          Description: '',
          SortID: 0,
          Responses: [virtualResponse],
          isCollapsed: false,
          ResponseStatus: Server.ResponseStatus.None,
        },
      ],
      isCollapsed: this.defaultSectionsCollapsed(),
      sectionUsers: [],
      ResponseStatus: Server.ResponseStatus.None,
      Type: Server.SectionType.StandAlone,
      SectionType: Dtos.TaskPlanSectionType.Mandatory,
    };
  }

  private _storeService = inject(StoreService);

  // Default collapse state for newly-built sections, driven by the BusinessUnit setting.
  private defaultSectionsCollapsed = computed(() => this._storeService.currentBu()?.ShowSectionsCollapsed ?? false);

  /**
   * Appends a virtual "Signatures" section containing the PSSR final approver signature
   * if pssr.showSignature is true and no Signatures section already exists.
   * Mirrors legacy addSignaturesSection() from pssr-edit-items.component.ts.
   */
  private appendSignaturesSection(sections: GroupedSection[]): GroupedSection[] {
    const pssr = this._storeService.currentPssr();

    if (!pssr?.SignatureGUID) {
      return sections;
    }

    const sortId = sections.length > 0 ? Math.max(...sections.map(s => s.SortID)) + 1 : 97;

    const caption = this._storeService.currentFinalApproverPssr();

    const virtualResponse: Dtos.PSSRResponse = {
      Id: null,
      PSSRID: pssr.Id,
      PSSRDescription: pssr.Name,
      TaskPlanDescription: '',
      SectionID: 0,
      SectionDescription: 'Signatures',
      SubSectionDescription: 'Signatures',
      ItemCode: '',
      ItemDescription: 'Signature',
      ResponseText: '',
      ActionItems: [
        {
          Id: 0,
          Images: [
            {
              Caption: caption,
              GUID: pssr.SignatureGUID,
              CreateDate: pssr.SignatureDate,
            },
          ],
        },
      ],
      SortID: 0,
      SectionSortID: sortId,
      SubSectionSortID: 0,
      GUID: '',
      CreateDate: new Date(),
      CreatedBy: '',
      UpdatedDate: new Date(),
      UpdatedBy: '',
      CanAnswer: false,
      ItemType: Server.ItemType.Signature,
      ShouldAddCommentToQuestion: false,
      DefaultSignatureDisclaimer: '',
      ResponseStatus: Server.ResponseStatus.Answered,
    } as Dtos.PSSRResponse;

    const existingSignaturesSectionIndex = sections.findIndex(
      s => s.Type === Server.SectionType.Signature && s.Description === 'Signatures'
    );

    if (existingSignaturesSectionIndex >= 0) {
      // Inject virtual response into the existing Signatures section
      const existing = sections[existingSignaturesSectionIndex];

      // Avoid adding twice (e.g. on silent reload)
      const alreadyInjected = existing.Responses.some(r => r.Id === null);
      if (alreadyInjected) {
        return sections;
      }

      const virtualSubSection: import('../models').GroupedSubSection = {
        Key: 'FinalApproverSignature',
        Description: '',
        SortID: 9999,
        Responses: [virtualResponse],
        isCollapsed: false,
        ResponseStatus: Server.ResponseStatus.Answered,
      };

      const updated = [...sections];
      updated[existingSignaturesSectionIndex] = {
        ...existing,
        Responses: [...existing.Responses, virtualResponse],
        SubSections: [...existing.SubSections, virtualSubSection],
      };
      return updated;
    }

    // No existing Signatures section — create a virtual one
    const virtualSubSection: GroupedSubSection = {
      Key: 'Signatures',
      Description: '',
      SortID: 0,
      Responses: [virtualResponse],
      isCollapsed: false,
      ResponseStatus: Server.ResponseStatus.Answered,
    };

    const signaturesSection: GroupedSection = {
      ID: 0,
      Description: 'Signatures',
      SortID: sortId,
      Responses: [virtualResponse],
      SubSections: [virtualSubSection],
      isCollapsed: this.defaultSectionsCollapsed(),
      sectionUsers: [],
      ResponseStatus: Server.ResponseStatus.Answered,
      Type: Server.SectionType.Signature,
      SectionType: Dtos.TaskPlanSectionType.Mandatory,
    };

    return [...sections, signaturesSection];
  }

  private appendProjectSpecificSection(sections: GroupedSection[]): GroupedSection[] {
    // Don't add if section already exists
    if (this.hasProjectSpecificSection(sections)) {
      return sections;
    }

    const pssr = this._storeService.currentPssr();

    // Only add for Draft or Initiated status
    if (!pssr || !this.isPssrDraftOrInitiated(pssr)) {
      return sections;
    }

    const projectSpecificSection = this.createProjectSpecificSection(sections);
    return [...sections, projectSpecificSection];
  }

  /**
   * Check if project specific section already exists
   */
  private hasProjectSpecificSection(sections: GroupedSection[]): boolean {
    return sections.some(s => s.Description === 'Project Specific');
  }

  /**
   * Check if PSSR is in Draft or Initiated status
   */
  private isPssrDraftOrInitiated(pssr: Dtos.PSSR): boolean {
    return [Server.PSSRStatus.Draft, Server.PSSRStatus.Initiated].includes(pssr.Status);
  }

  /**
   * Create project specific section
   */
  private createProjectSpecificSection(sections: GroupedSection[]): GroupedSection {
    const sortID = sections.length > 0 ? Math.max(...sections.map(s => s.SortID)) + 1 : 1;

    return {
      ID: 0,
      Description: 'Project Specific',
      SortID: sortID,
      Responses: [],
      SubSections: [
        {
          Key: '0-Project Specific-0-0',
          Description: '',
          SortID: 0,
          Responses: [],
          isCollapsed: false,
          ResponseStatus: Server.ResponseStatus.None,
        },
      ],
      isCollapsed: this.defaultSectionsCollapsed(),
      sectionUsers: [],
      ResponseStatus: Server.ResponseStatus.None,
      Type: Server.SectionType.ProjectSpecific,
      SectionType: Dtos.TaskPlanSectionType.Mandatory,
    };
  }

  /**
   * Extract a single section with its subsections and calculate status
   * @param sectionKey - Unique key for the section
   * @param sectionResponses - All responses belonging to this section
   * @param allTaskPlanSections - All task plan sections for type lookup
   * @returns Grouped section with calculated status
   */
  private extractSection(
    sectionKey: string,
    sectionResponses: Dtos.PSSRResponse[],
    allTaskPlanSections: Dtos.TaskPlanSection[]
  ): GroupedSection {
    const response = sectionResponses.find(r => `${r.SectionID}-${r.SectionDescription}` === sectionKey);

    if (!response) {
      throw new Error(`No response found for section key: ${sectionKey}`);
    }

    const subSections = this.extractSubSections(sectionResponses);

    const section: GroupedSection = {
      ID: response.SectionID,
      Description: response.SectionDescription,
      SortID: response.SectionSortID,
      Responses: sectionResponses,
      SubSections: subSections,
      isCollapsed: this.defaultSectionsCollapsed(),
      sectionUsers: [],
      ResponseStatus: this.calculateSectionStatus(sectionResponses, subSections),
      ...this.getTaskPlanSectionType(allTaskPlanSections, response),
    };

    return section;
  }

  private getTaskPlanSectionType(
    allTaskPlanSections: Dtos.TaskPlanSection[],
    response: Dtos.PSSRResponse
  ): Pick<GroupedSection, 'Type' | 'SectionType'> {
    // Find matching TaskPlanSection to get Type and SectionType
    if (response.SectionDescription === 'Project Specific') {
      return {
        SectionType: Dtos.TaskPlanSectionType.Mandatory,
        Type: Server.SectionType.ProjectSpecific,
      };
    }

    const taskPlanSection = allTaskPlanSections.find(
      s => s.ID === response.SectionID || s.Description === response.SectionDescription
    );
    const result: Pick<GroupedSection, 'Type' | 'SectionType'> = {
      SectionType: taskPlanSection?.SectionType,
      Type: Server.SectionType.Body,
    };

    if (response.SectionDescription === 'Signatures') {
      result.Type = Server.SectionType.Signature;
    }

    return result;
  }

  /**
   * Extract subsections from section responses and calculate their statuses
   * @param sectionResponses - All responses belonging to a section
   * @returns Array of grouped subsections with calculated statuses
   */
  private extractSubSections(sectionResponses: Dtos.PSSRResponse[]): GroupedSubSection[] {
    let subSections: GroupedSubSection[] = [];

    // Group responses by subsection
    for (const sectionResponse of sectionResponses) {
      const subSectionKey = this.getSubSectionKey(sectionResponse);
      const existingSubSection = subSections.find(ss => ss.Key === subSectionKey);

      if (existingSubSection) {
        existingSubSection.Responses.push(sectionResponse);
      } else {
        subSections.push({
          Key: subSectionKey,
          Description: sectionResponse.SubSectionDescription,
          SortID: sectionResponse.SubSectionSortID,
          Responses: [sectionResponse],
          isCollapsed: false,
          ResponseStatus: Server.ResponseStatus.None,
        });
      }
    }

    // Sort subsections by SortID
    subSections = this.sort(subSections, this.sortBySortId);

    // Calculate status and sort responses for each subsection
    for (const subSection of subSections) {
      // Sort responses within subsection
      subSection.Responses = this.sort(subSection.Responses, this.sortBySortId);

      // Calculate subsection status (worst status from all responses)
      const statuses = subSection.Responses.map(r => r.ResponseStatus || Server.ResponseStatus.None);
      subSection.ResponseStatus =
        statuses.length > 0 ? (Math.max(...statuses) as Server.ResponseStatus) : Server.ResponseStatus.None;
    }

    return subSections;
  }

  /**
   * Sort array using provided comparison function
   * @param array - Array to sort
   * @param sortFn - Comparison function
   * @returns New sorted array
   */
  private sort<T>(array: T[], sortFn: (a: T, b: T) => number): T[] {
    return [...array].sort(sortFn);
  }

  /**
   * Generate unique key for subsection identification
   * @param response - PSSR response
   * @returns Unique subsection key
   */
  private getSubSectionKey(response: Dtos.PSSRResponse): string {
    return `${response.SectionID}-${response.SectionDescription}-${response.SubSectionDescription}-${response.SubSectionSortID}`;
  }

  /**
   * Calculate section response status after grouping
   * Section status is determined by the "worst" (maximum) status from:
   * - All direct responses in the section
   * - All subsection statuses (which are aggregated from their responses)
   *
   * Status hierarchy (worst to best):
   * - NeedUserAttention (3) > Optional (2) > Answered (1) > None (0)
   *
   * @param responses - Array of responses in the section
   * @param subSections - Array of subsections in the section
   * @returns Calculated ResponseStatus for the section
   */
  private calculateSectionStatus(
    responses: Dtos.PSSRResponse[],
    subSections: GroupedSubSection[]
  ): Server.ResponseStatus {
    const responseStatuses = responses.map(r => r.ResponseStatus || Server.ResponseStatus.None);
    const maxResponseStatus = responseStatuses.length > 0 ? Math.max(...responseStatuses) : Server.ResponseStatus.None;

    const subsectionStatuses = subSections.map(ss => ss.ResponseStatus || Server.ResponseStatus.None);
    const maxSubsectionStatus =
      subsectionStatuses.length > 0 ? Math.max(...subsectionStatuses) : Server.ResponseStatus.None;

    return Math.max(maxResponseStatus, maxSubsectionStatus) as Server.ResponseStatus;
  }
}
